import React from 'react';
import { Button, Col, Divider, Input, InputNumber, Row, Select, Space, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

type JsonSchemaLike = {
  type?: string;
  required?: string[];
  properties?: Record<string, JsonSchemaLike>;
  items?: JsonSchemaLike;
  minItems?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
  enum?: Array<string | number | boolean>;
  title?: string;
  description?: string;
  default?: any;
};

interface ModuleConfigEditorProps {
  schema?: JsonSchemaLike | null;
  defaultValue?: any;
  value?: any;
  onChange?: (value: any) => void;
  footerText?: string | null;
}

const isObject = (value: any): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const emptyValueForSchema = (schema?: JsonSchemaLike | null): any => {
  if (!schema) return undefined;
  if (schema.default !== undefined) return schema.default;
  if (schema.type === 'object') {
    return {};
  }
  if (schema.type === 'array') {
    const length = schema.minItems || 0;
    return Array.from({ length }).map(() => emptyValueForSchema(schema.items));
  }
  if (schema.type === 'boolean') return false;
  if (schema.type === 'number' || schema.type === 'integer') return schema.minimum ?? 0;
  if (schema.enum?.length) return schema.enum[0];
  return '';
};

const setAtPath = (source: any, path: Array<string | number>, nextValue: any): any => {
  if (path.length === 0) return nextValue;
  const [head, ...rest] = path;
  if (typeof head === 'number') {
    const base = Array.isArray(source) ? [...source] : [];
    base[head] = setAtPath(base[head], rest, nextValue);
    return base;
  }
  const base = isObject(source) ? { ...source } : {};
  base[head] = setAtPath(base[head], rest, nextValue);
  return base;
};

const getAtPath = (source: any, path: Array<string | number>): any => (
  path.reduce((current, key) => current?.[key], source)
);

const titleForKey = (key: string): string => (
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase())
);

const deepMergeConfig = (base: any, override: any): any => {
  if (Array.isArray(base)) {
    return Array.isArray(override) ? override : base;
  }
  if (isObject(base)) {
    const next: Record<string, any> = { ...base };
    if (isObject(override)) {
      Object.entries(override).forEach(([key, childValue]) => {
        next[key] = deepMergeConfig(next[key], childValue);
      });
    }
    return next;
  }
  return override !== undefined && override !== null ? override : base;
};

const fieldWidth = { width: '100%' };

const compactFieldStyle = { marginBottom: 8 };

const sectionStyle: React.CSSProperties = {
  border: '1px solid #eef1f5',
  borderRadius: 8,
  padding: 10,
  background: '#fbfcfe',
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 8,
};

const tableWrapStyle: React.CSSProperties = {
  border: '1px solid #e6e9ef',
  borderRadius: 8,
  overflowX: 'auto',
  background: '#fff',
};

const compactTableStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 760,
  borderCollapse: 'collapse',
};

const tableCellStyle: React.CSSProperties = {
  borderBottom: '1px solid #f0f0f0',
  padding: 6,
  verticalAlign: 'top',
};

const primitiveColumnSpan = (nodeSchema: JsonSchemaLike): number => {
  if (nodeSchema.type === 'boolean') return 4;
  if (nodeSchema.enum?.length) return 6;
  if (nodeSchema.type === 'number' || nodeSchema.type === 'integer') return 6;
  return 8;
};

const ModuleConfigEditor: React.FC<ModuleConfigEditorProps> = ({ schema, defaultValue, value, onChange, footerText }) => {
  const effectiveValue = React.useMemo(
    () => deepMergeConfig(defaultValue ?? emptyValueForSchema(schema), value),
    [defaultValue, schema, value],
  );

  const update = React.useCallback((path: Array<string | number>, nextValue: any) => {
    onChange?.(setAtPath(effectiveValue ?? emptyValueForSchema(schema), path, nextValue));
  }, [effectiveValue, onChange, schema]);

  const renderPrimitive = React.useCallback((
    nodeSchema: JsonSchemaLike,
    path: Array<string | number>,
    label: string,
    required = false,
    showLabel = true,
  ): React.ReactNode => {
    const currentValue = getAtPath(effectiveValue, path);
    const labelNode = (
      <Text strong={path.length <= 1} style={{ fontSize: path.length <= 1 ? 13 : 12 }}>
        {nodeSchema.title || label}{required ? ' *' : ''}
      </Text>
    );

    if (nodeSchema.enum?.length) {
      return (
        <Space direction="vertical" size={3} style={fieldWidth}>
          {showLabel && labelNode}
          <Select
            size="small"
            value={currentValue}
            onChange={(next) => update(path, next)}
            options={nodeSchema.enum.map((option) => ({ value: option as any, label: String(option) }))}
            style={fieldWidth}
          />
        </Space>
      );
    }

    if (nodeSchema.type === 'boolean') {
      return (
        <Space align="center" style={{ minHeight: 30 }}>
          <Switch size="small" checked={currentValue === true} onChange={(next) => update(path, next)} />
          {showLabel && labelNode}
        </Space>
      );
    }

    if (nodeSchema.type === 'number' || nodeSchema.type === 'integer') {
      return (
        <Space direction="vertical" size={3} style={fieldWidth}>
          {showLabel && labelNode}
          <InputNumber
            size="small"
            value={typeof currentValue === 'number' ? currentValue : undefined}
            min={nodeSchema.minimum}
            max={nodeSchema.maximum}
            precision={nodeSchema.type === 'integer' ? 0 : undefined}
            onChange={(next) => update(path, next)}
            style={fieldWidth}
          />
        </Space>
      );
    }

    return (
      <Space direction="vertical" size={3} style={fieldWidth}>
        {showLabel && labelNode}
        <Input size="small" value={currentValue ?? ''} onChange={(event) => update(path, event.target.value)} />
      </Space>
    );
  }, [effectiveValue, update]);

  const renderNode = React.useCallback((nodeSchema: JsonSchemaLike | undefined, path: Array<string | number>, label: string, parentRequired: string[] = []): React.ReactNode => {
    if (!nodeSchema) {
      return null;
    }
    const required = typeof path[path.length - 1] === 'string' && parentRequired.includes(path[path.length - 1] as string);
    const labelNode = (
      <Text strong={path.length <= 1} style={{ fontSize: path.length <= 1 ? 13 : 12 }}>
        {nodeSchema.title || label}{required ? ' *' : ''}
      </Text>
    );

    if (nodeSchema.enum?.length || nodeSchema.type === 'boolean' || nodeSchema.type === 'number' || nodeSchema.type === 'integer' || nodeSchema.type === 'string' || !nodeSchema.type) {
      return renderPrimitive(nodeSchema, path, label, required);
    }

    if (nodeSchema.type === 'array') {
      const currentValue = getAtPath(effectiveValue, path);
      const items = Array.isArray(currentValue) ? currentValue : [];
      const minItems = nodeSchema.minItems || 0;
      const maxItems = nodeSchema.maxItems;
      const fixedLength = maxItems !== undefined && maxItems === minItems;
      const normalizedItems = items.length >= minItems
        ? items
        : [...items, ...Array.from({ length: minItems - items.length }).map(() => emptyValueForSchema(nodeSchema.items))];

      const arrayObjectEntries = nodeSchema.items?.type === 'object' || nodeSchema.items?.properties
        ? Object.entries(nodeSchema.items.properties || {})
        : null;
      const objectRequired = nodeSchema.items?.required || [];

      if (arrayObjectEntries?.length) {
        return (
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <Space direction="vertical" size={0}>
                {labelNode}
                {fixedLength && <Text type="secondary" style={{ fontSize: 11 }}>Exactly {minItems} rows</Text>}
              </Space>
              {!fixedLength && (maxItems === undefined || normalizedItems.length < maxItems) && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => update(path, [...normalizedItems, emptyValueForSchema(nodeSchema.items)])}
                >
                  Add
                </Button>
              )}
            </div>
            <div style={tableWrapStyle}>
              <table style={compactTableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...tableCellStyle, width: 48, textAlign: 'left' }}>#</th>
                    {arrayObjectEntries.map(([key, childSchema]) => (
                      <th key={key} style={{ ...tableCellStyle, textAlign: 'left', minWidth: childSchema.type === 'boolean' ? 120 : 150 }}>
                        <Text style={{ fontSize: 12 }}>{childSchema.title || titleForKey(key)}</Text>
                      </th>
                    ))}
                    {!fixedLength && <th style={{ ...tableCellStyle, width: 48 }} />}
                  </tr>
                </thead>
                <tbody>
                  {normalizedItems.map((_, index) => (
                    <tr key={index}>
                      <td style={tableCellStyle}><Text type="secondary">{index + 1}</Text></td>
                      {arrayObjectEntries.map(([key, childSchema]) => (
                        <td key={key} style={tableCellStyle}>
                          {childSchema.type === 'object' || childSchema.type === 'array'
                            ? renderNode(childSchema, [...path, index, key], titleForKey(key), objectRequired)
                            : renderPrimitive(childSchema, [...path, index, key], titleForKey(key), objectRequired.includes(key), false)}
                        </td>
                      ))}
                      {!fixedLength && (
                        <td style={tableCellStyle}>
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={normalizedItems.length <= minItems}
                            onClick={() => update(path, normalizedItems.filter((__, itemIndex) => itemIndex !== index))}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      return (
        <div style={sectionStyle}>
          <Space direction="vertical" size="small" style={fieldWidth}>
            {normalizedItems.map((_, index) => (
              <div
                key={index}
                style={{ borderBottom: index === normalizedItems.length - 1 ? undefined : '1px solid #eef1f5', paddingBottom: 8 }}
              >
                <Space align="start" style={fieldWidth}>
                  <div style={{ flex: 1 }}>
                    {renderNode(nodeSchema.items, [...path, index], `Item ${index + 1}`)}
                  </div>
                  {!fixedLength && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={normalizedItems.length <= minItems}
                    onClick={() => {
                      const next = normalizedItems.filter((__, itemIndex) => itemIndex !== index);
                      update(path, next);
                    }}
                  />
                )}
                </Space>
              </div>
            ))}
            {!fixedLength && (maxItems === undefined || normalizedItems.length < maxItems) && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => update(path, [...normalizedItems, emptyValueForSchema(nodeSchema.items)])}
              >
                Add {label.toLowerCase()}
              </Button>
            )}
            {fixedLength && <Text type="secondary">Exactly {minItems} item{minItems === 1 ? '' : 's'} required.</Text>}
          </Space>
        </div>
      );
    }

    if (nodeSchema.type === 'object' || nodeSchema.properties) {
      const entries = Object.entries(nodeSchema.properties || {});
      const content = (
        <Row gutter={[12, 4]}>
          {entries.map(([key, childSchema]) => {
            const wide = childSchema.type === 'object' || childSchema.type === 'array';
            return (
              <Col xs={24} md={wide ? 24 : primitiveColumnSpan(childSchema)} xl={wide ? 24 : Math.min(primitiveColumnSpan(childSchema), 6)} key={key}>
                <div style={compactFieldStyle}>
                  {renderNode(childSchema, [...path, key], titleForKey(key), nodeSchema.required || [])}
                </div>
              </Col>
            );
          })}
        </Row>
      );

      if (path.length === 0) {
        return content;
      }
      return (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>{labelNode}</div>
          {content}
        </div>
      );
    }

    return renderPrimitive(nodeSchema, path, label, required);
  }, [effectiveValue, renderPrimitive, update]);

  if (!schema) {
    return null;
  }

  return (
    <div>
      {renderNode(schema, [], 'Configuration', schema.required || [])}
      {footerText !== null ? (
        <>
          <Divider style={{ margin: '8px 0 0' }} />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {footerText || 'These values are stored on the physical device module and override catalogue defaults.'}
          </Text>
        </>
      ) : null}
    </div>
  );
};

export { deepMergeConfig };
export default ModuleConfigEditor;
